import { useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';

import { Toast } from '../../../components';
import { useQuizEmbedSession, useQuizImport } from '../hooks/index.ts';
import type { AiTabbedModalProps } from '../types/modal.ts';

// Tải trễ (Lazy load) QuizEmbed để tối ưu dung lượng bundle ban đầu
// Thư viện marked + katex chỉ được nạp về client khi giáo viên mở giao diện "Tạo Câu Hỏi"
const QuizEmbed = lazy(() => import('../embed/QuizEmbed.tsx').then((m) => ({ default: m.QuizEmbed })));

/**
 * Modal Trợ lý AI trên bảng dạy:
 * Hiển thị giao diện tạo câu hỏi trắc nghiệm thông qua iframe AI Quiz Supporter (QuizEmbed)
 */
export function AiTabbedModal({ open, onClose, onImportSuccess }: AiTabbedModalProps) {
  const { authUser, draftChatId, handleChatIdChange } = useQuizEmbedSession();
  const { notice, noticeKind, setNotice, handleQuizImport } = useQuizImport(onClose, onImportSuccess);

  // Xử lý phím tắt Escape để đóng modal
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const content = (
    <Suspense
      fallback={
        <div className="quiz-embed-layer" role="dialog" aria-modal="true" aria-label="Đang tải">
          <div className="quiz-embed-panel">
            <div className="quiz-embed-panel__header">
              <h3>Tạo Câu Hỏi</h3>
            </div>
            <div className="quiz-embed-panel__body">
              <div className="quiz-embed-loading">
                <span>Đang tải...</span>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <QuizEmbed
        open={true}
        authUser={authUser}
        draftChatId={draftChatId}
        onChatIdChange={handleChatIdChange}
        onImport={handleQuizImport}
        onClose={onClose}
      />
    </Suspense>
  );

  return (
    <>
      {createPortal(content, document.body)}
      <Toast open={Boolean(notice)} kind={noticeKind} message={notice} onClose={() => setNotice('')} />
    </>
  );
}

// Định danh phụ tương thích ngược
export { AiTabbedModal as AiModal };
